'use client'

import { getWallets, SalesWallet } from '@/lib/api/sales/wallets-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'
import { MdAccountBalanceWallet, MdClose, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (walletId: number | null, wallet: SalesWallet | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    /** Carteiras a ocultar — normalmente a de origem, numa transferência. */
    excludeIds?: number[]
    /** Inclui as inativas. Default: só ativas, que são as que podem receber. */
    includeInactive?: boolean
    /** `sm` alinha com barras de filtro; `md` é o padrão de formulário. */
    size?: 'sm' | 'md'
}

/**
 * Seletor de carteira de clientes.
 *
 * Carteira é global por organização, então não há escopo de filial a informar —
 * a lista já vem da organização do token. Criado para ser reusado em toda ação
 * de mover cliente entre carteiras.
 */
export function WalletSelector({
    value,
    onChange,
    label = 'Carteira',
    placeholder = 'Buscar carteira...',
    disabled = false,
    className = '',
    excludeIds = [],
    includeInactive = false,
    size = 'md',
}: Props) {
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [loading, setLoading] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)

    const load = async () => {
        if (loading || loaded) return
        setLoading(true)
        try {
            const response = await getWallets()
            if (response.success) setWallets(response.result || [])
            setLoaded(true)
        } finally {
            setLoading(false)
        }
    }

    // Resolve o `value` recebido de fora para o registro completo: sem isso o
    // campo exibiria o id cru quando a tela monta com uma carteira já escolhida.
    useEffect(() => {
        if (value && !loaded && !loading) load()
    }, [value, loaded, loading])

    // Painel em portal, ancorado no campo: dentro de um modal (`modal-box` tem
    // overflow) o dropdown `absolute` era cortado e as carteiras ficavam
    // inalcançáveis. O fechar-ao-clicar-fora mora no hook porque o painel deixa de
    // estar dentro do container — o handler antigo o trataria como clique fora e
    // fecharia antes de a opção ser escolhida.
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown,
        closeDropdown
    )

    const available = useMemo(
        () =>
            wallets.filter(
                w => !excludeIds.includes(w.id) && (includeInactive || w.active)
            ),
        [wallets, excludeIds, includeInactive]
    )

    const selected = useMemo(
        () => wallets.find(w => w.id === value) ?? null,
        [wallets, value]
    )

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase()
        if (!q) return available
        return available.filter(w => w.name.toLowerCase().includes(q))
    }, [available, searchTerm])

    const isSm = size === 'sm'
    const resolving = !!value && !selected && loading

    return (
        <div className={`form-control w-full ${className}`} ref={anchorRef}>
            {label && (
                <label className="label pb-1">
                    <span className="label-text">{label}</span>
                </label>
            )}

            {resolving ? (
                <div
                    className={
                        'flex items-center gap-2 input input-bordered w-full text-base-content/60 ' +
                        (isSm ? 'input-sm!' : '')
                    }
                >
                    <span className="loading loading-spinner loading-xs" />
                    <span className="truncate">Carregando...</span>
                </div>
            ) : selected ? (
                <div className="relative">
                    <div
                        className={
                            'flex items-center gap-2 input input-bordered w-full pr-9 ' +
                            (isSm ? 'input-sm!' : '')
                        }
                    >
                        <MdAccountBalanceWallet className="text-base-content/60 flex-shrink-0" />
                        <span className="truncate">{selected.name}</span>
                        {!selected.active && (
                            <span className="badge badge-xs badge-ghost">
                                Inativa
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                        onClick={() => {
                            onChange(null, null)
                            setSearchTerm('')
                        }}
                        disabled={disabled}
                    >
                        <MdClose />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (isSm ? 'input-sm!' : '')
                            }
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={() => {
                                setShowDropdown(true)
                                load()
                            }}
                            disabled={disabled}
                        />
                    </div>
                    {renderPanel(
                        <>
                            {loading && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Carregando...
                                </div>
                            )}
                            {!loading && filtered.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhuma carteira encontrada
                                </div>
                            )}
                            {!loading &&
                                filtered.map(wallet => (
                                    <button
                                        key={wallet.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => {
                                            onChange(wallet.id, wallet)
                                            setShowDropdown(false)
                                            setSearchTerm('')
                                        }}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-medium text-sm truncate">
                                                {wallet.name}
                                            </span>
                                            <span className="text-xs text-base-content/60 flex-shrink-0">
                                                {wallet._count?.clients ?? 0}{' '}
                                                clientes
                                            </span>
                                        </div>
                                    </button>
                                ))}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
